import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { employeeNavForWorkspace, navChildPanelAllows, submenuChildren } from './employeeNav';
import { canUsePayrollRecords, canViewPayrollRecords } from '../hrms/pages/Payroll/payrollAccess';
import { canManageWorkflow } from '../hrms/pages/Workflow/workflowAccess';
import { isLimitedWorkspace } from '../utils/limitedAccess';
import { isElevated } from '../hrms/utils/rolePermissions';
import { COMPLIANCE_PATH } from '../hrms/api/compliance';
import { getMyAssignedLioIssues } from '../hrms/api/leoIssues';

const MIN_WIDTH = 168;
const MAX_WIDTH = 320;
const DEFAULT_WIDTH = 198;
const STORAGE_KEY = 'teamlio:employeeNavWidth';

function readStoredWidth(): number {
  try {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(saved) && saved >= MIN_WIDTH && saved <= MAX_WIDTH) return saved;
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_WIDTH;
}

export default function EmployeeNavPanel() {
  const { user } = useAuth();
  const { hasAccess, isPanelVisible, isPathVisible, canManage } = useAccess();
  const location = useLocation();
  const [width, setWidth] = useState<number>(readStoredWidth);
  const draggingRef = useRef(false);
  const canShowPayrollChildren = canUsePayrollRecords(user?.role, { hasAccess, canManage });
  const canShowPayrollRecords = canViewPayrollRecords(user?.role, {
    hasAccess,
    isVisible: isPanelVisible('payroll-records'),
  });

  // "My Lio Questions" has no Access panel to gate it (the endpoint is
  // self-only), and isPathVisible is permissive for a path it cannot
  // identify — so it would show for everyone. Gate it on actually having one,
  // the same way hrpayroll's Sidebar does.
  const [hasLioIssues, setHasLioIssues] = useState(false);
  useEffect(() => {
    let active = true;
    getMyAssignedLioIssues()
      .then((rows) => {
        if (active) setHasLioIssues(rows.length > 0);
      })
      .catch(() => {
        /* never block the nav on it — the entry just stays hidden */
      });
    return () => {
      active = false;
    };
  }, [user]);

  const items = employeeNavForWorkspace(isLimitedWorkspace(user?.subscription))
    .filter((c) => c.to !== '/employee/lio-questions' || hasLioIssues)
    .filter((c) => c.to !== COMPLIANCE_PATH || hasAccess || isElevated(user?.role))
    .filter((c) => !hasAccess || isPathVisible(c.to));
  if (items.length === 0) return null;

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    if (draggingRef.current) return;
    draggingRef.current = true;
    const startX = e.clientX;
    const startW = width;
    let finalW = startW;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      finalW = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + (ev.clientX - startX)));
      setWidth(finalW);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try {
        localStorage.setItem(STORAGE_KEY, String(Math.round(finalW)));
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const resetWidth = () => {
    setWidth(DEFAULT_WIDTH);
    try {
      localStorage.setItem(STORAGE_KEY, String(DEFAULT_WIDTH));
    } catch {
      /* ignore */
    }
  };

  return (
    <aside
      className="gt-sidebar gt-task-panel"
      style={{ width, flexShrink: 0, position: 'relative' }}
    >
      <div className="gt-sidebar-header">
        <span className="gt-sidebar-brand">Employee Hub</span>
      </div>

      <nav className="gt-sidebar-nav">
        {items.map(({ to, label, Icon, children }) => {
          const isPayroll = to === '/employee/payroll';
          const visibleChildren = (children || []).filter((child) => {
            // The role list only stands in when there is no Access profile;
            // with one, `panel` below decides (Manage on that grid cell).
            if (child.managerOnly && !hasAccess && !canManageWorkflow(user?.role)) return false;
            // Access-grid gate for admin surfaces (e.g. Kiosks). Applied before
            // the fail-open `hasAccess` check below on purpose: a profiled user
            // without MANAGE must not see it even though the path itself is
            // visible to them.
            if (!navChildPanelAllows(child, { canManage, hasAccess })) return false;
            // Own payslip / own tax summary: not Access panels in TeamLio's
            // panelCatalog, so nothing can hide them.
            if (child.selfService) return true;
            if (hasAccess && !isPathVisible(child.to)) return false;
            // The org-wide payroll entries still need the manage-level grant.
            if (isPayroll && !canShowPayrollChildren) return false;
            return true;
          });
          const shownChildren = submenuChildren(to, visibleChildren);
          const childActive = shownChildren.some((child) =>
            location.pathname === child.to || location.pathname.startsWith(child.to + '/')
          );
          const expanded = shownChildren.length > 0 && childActive;

          return (
            <div key={to}>
              <NavLink
                to={to}
                className={({ isActive }) => `gt-sidebar-item${isActive || childActive ? ' is-active' : ''}`}
              >
                <Icon className="gt-sidebar-icon" />
                <span className="gt-sidebar-label">{label}</span>
                {shownChildren.length > 0 ? (
                  <ChevronDown
                    className="ml-auto h-4 w-4 shrink-0"
                    style={{
                      color: 'var(--gt-text-tertiary)',
                      transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 120ms ease',
                    }}
                  />
                ) : null}
              </NavLink>

              {expanded ? (
                <div
                  className="ml-5 mt-1 flex flex-col gap-1 border-l pl-3"
                  style={{ borderColor: 'var(--gt-divider)' }}
                >
                  {shownChildren.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      end={child.to === to}
                      className={({ isActive }) => `gt-sidebar-item${isActive ? ' is-active' : ''}`}
                      style={{
                        minHeight: 36,
                        padding: '0.45rem 0.75rem',
                        fontSize: 13,
                      }}
                    >
                      <span className="gt-sidebar-label">
                        {child.adminLabel && canShowPayrollRecords ? child.adminLabel : child.label}
                      </span>
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      <div
        className="gt-task-panel__resize"
        onMouseDown={startResize}
        onDoubleClick={resetWidth}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize employee hub panel"
        title="Drag to resize · double-click to reset"
        style={{
          position: 'absolute',
          top: 0,
          right: -3,
          width: 7,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 5,
        }}
      />
    </aside>
  );
}
